/**
 * useActiveRentals — Custom hook for loading and filtering active rentals
 * Extracted from ActiveRentals.jsx to improve maintainability
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { rentalApi } from "@/lib/api";

export function useActiveRentals() {
  const [rentals, setRentals] = useState([]);
  const [filteredRentals, setFilteredRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const loadActiveRentals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rentalApi.getAll({ status: "active" });
      const data = response.data || [];
      setRentals(data);
      setFilteredRentals(data);
    } catch {
      toast.error("Error al cargar alquileres");
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter rentals whenever search query or list changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredRentals(rentals);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFilteredRentals(
      rentals.filter(
        (r) =>
          r.customer_name?.toLowerCase().includes(q) ||
          r.customer_dni?.toLowerCase().includes(q) ||
          r.items?.some(
            (i) =>
              i.internal_code?.toLowerCase().includes(q) ||
              i.barcode?.toLowerCase().includes(q)
          )
      )
    );
  }, [searchQuery, rentals]);

  // Initial load
  useEffect(() => {
    loadActiveRentals();
  }, [loadActiveRentals]);

  /**
   * Update a single rental in the local list without a full reload.
   * Pass the full updated rental object.
   */
  const updateRentalLocally = useCallback((updatedRental) => {
    setRentals((prev) =>
      prev.map((r) => (r.id === updatedRental.id ? updatedRental : r))
    );
  }, []);

  /**
   * Remove a rental from the local list (e.g., after return).
   */
  const removeRentalLocally = useCallback((rentalId) => {
    setRentals((prev) => prev.filter((r) => r.id !== rentalId));
  }, []);

  return {
    rentals,
    filteredRentals,
    setFilteredRentals,
    loading,
    searchQuery,
    setSearchQuery,
    loadActiveRentals,
    updateRentalLocally,
    removeRentalLocally,
  };
}
